import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import AuthenticatedLayout from './AuthenticatedLayout'
import LoadingSpinner from './UI/LoadingSpinner'
import LoginRequiredPanel from './auth/LoginRequiredPanel'
import AuthError from './auth/AuthError'
import AccessDenied from './auth/AccessDenied'
import LoginPrompt from './auth/LoginPrompt'

// Check if user has completed their profile (has a rave alias)
const isProfileComplete = (user) => {
  return user?.profileSettings?.raveName && user.profileSettings.raveName.trim().length > 0
}

const ProtectedRoute = ({
  children,
  fallback = null,
  loadingComponent = <div className="flex items-center justify-center min-h-screen"><LoadingSpinner text="Loading..." /></div>,
  loginComponent = <LoginRequiredPanel />,
  errorComponent = null,
  requireAdmin = false,
  requireProfileComplete = true,
  message = null
}) => {
  const {
    isAuthenticated,
    isLoading,
    isError,
    user,
    error,
    checkAuthStatus,
    clearError
  } = useAuth()
  const location = useLocation()

  const handleRetry = () => {
    clearError()
    checkAuthStatus()
  }

  if (isLoading) {
    return loadingComponent
  }

  if (isError) {
    if (errorComponent) {
      return errorComponent
    }
    return <AuthError error={error} onRetry={handleRetry} />
  }

  if (!isAuthenticated) {
    if (fallback) {
      return fallback
    }
    return React.cloneElement(loginComponent, { message })
  }

  if (requireAdmin && user && user.role !== 'admin') {
    return <AccessDenied />
  }

  // Redirect to onboarding if profile is incomplete and not already on onboarding page
  if (requireProfileComplete && !isProfileComplete(user) && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />
  }

  return (
    <AuthenticatedLayout>
      {children}
    </AuthenticatedLayout>
  )
}

export const withProtection = (Component, options = {}) => {
  return (props) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  )
}

export const AdminRoute = ({ children, ...props }) => {
  return (
    <ProtectedRoute requireAdmin={true} {...props}>
      {children}
    </ProtectedRoute>
  )
}

export const OptionallyProtectedRoute = ({ children, loginPrompt = null }) => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner text="Loading..." /></div>
  }

  return (
    <>
      {!isAuthenticated && loginPrompt && (
        <LoginPrompt message={loginPrompt} />
      )}
      {children}
    </>
  )
}

export default ProtectedRoute